import { useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Camera, Loader2, Pencil, X, Check, Copy, Globe, ShieldCheck, ShieldAlert,
  MapPin, Award, FileText, Building2, BadgeCheck, Lock,
} from 'lucide-react'
import type { JournalistProfileState } from './useJournalistProfile'

const TIER_STYLES: Record<string, string> = {
  gold:   'bg-accent/15 text-accent border-accent/40',
  silver: 'bg-muted text-muted-foreground border-border',
  bronze: 'bg-warning/15 text-warning border-warning/40',
  none:   'bg-muted/50 text-muted-foreground border-border',
}

function Stat({ icon: Icon, label, value }: { icon: any; label: string; value: string | number }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-card border border-border transition-colors hover:border-accent/50">
      <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="min-w-0">
        <div className="text-lg font-semibold leading-none tabular-nums">{value}</div>
        <div className="text-[11px] text-muted-foreground mt-1 truncate">{label}</div>
      </div>
    </div>
  )
}

export function ProfileHero({ state }: { state: JournalistProfileState }) {
  const { profile, score, accreditations, submissions, editing, setEditing, uploadingAvatar, uploadAvatar, overallRanking } = state
  const avatarInputRef = useRef<HTMLInputElement>(null)
  const [copied, setCopied] = useState(false)

  const tier = (score?.badge_tier as string) ?? 'none'
  const total = Math.round(score?.score_total ?? 0)
  const activeAccreditations = (accreditations ?? []).filter((a: any) => a.status === 'active').length
  const photoStatus = profile?.photo_status ?? 'pending'
  const photoLocked = photoStatus !== 'reupload_requested' && (photoStatus === 'approved' || activeAccreditations > 0)
  const docsDone = ['doc_national_id', 'doc_press_card', 'doc_assignment']
    .filter(k => !!(profile as any)?.[k]).length
  const publicUrl = profile?.slug ? `${window.location.origin}/press/${profile.slug}` : null

  return (
    <div className="space-y-4">
      <div className="relative overflow-hidden rounded-2xl border border-border">
        <div className="h-24 bg-gradient-to-r from-primary/80 via-primary to-accent/70" />
        <div className="p-5 pt-0 bg-card">
          <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-10">
            <div className="relative shrink-0 group">
              {profile?.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt={profile.full_name || 'Profile photo'}
                  className="w-24 h-24 rounded-2xl object-cover border-4 border-card shadow-lg"
                />
              ) : (
                <div className="w-24 h-24 rounded-2xl bg-muted border-4 border-card shadow-lg flex items-center justify-center">
                  <Camera className="w-6 h-6 text-muted-foreground" />
                </div>
              )}
              {photoLocked ? (
                <div
                  className="absolute inset-0 rounded-2xl bg-foreground/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1 text-center px-2"
                  aria-label="Photo locked"
                >
                  <Lock className="w-5 h-5 text-background" />
                  <span className="text-[9px] text-background leading-tight">Locked — approved by your institution</span>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => avatarInputRef.current?.click()}
                  disabled={uploadingAvatar}
                  className="absolute inset-0 rounded-2xl bg-foreground/60 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity flex items-center justify-center"
                  aria-label="Change profile photo"
                >
                  {uploadingAvatar
                    ? <Loader2 className="w-5 h-5 animate-spin text-background" />
                    : <Camera className="w-5 h-5 text-background" />}
                </button>
              )}
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) uploadAvatar(f); e.target.value = '' }}
              />
            </div>

            <div className="flex-1 min-w-0 sm:pb-1">
              {photoStatus === 'reupload_requested' && (
                <div className="mb-2 flex items-start gap-1.5 text-[11px] text-warning bg-warning/10 border border-warning/30 rounded-lg px-2.5 py-1.5">
                  <ShieldAlert className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>
                    Your institution has requested a new photo{profile?.photo_review_notes ? `: ${profile.photo_review_notes}` : '.'} Upload a passport-style photo on a plain white background.
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-bold leading-tight truncate">
                  {profile?.full_name || 'Your name'}
                </h1>
                {profile?.outlet_verified
                  ? <Badge variant="outline" className="gap-1 text-[10px] bg-success/15 text-success border-success/30">
                      <ShieldCheck className="w-3 h-3" /> Verified outlet
                    </Badge>
                  : <Badge variant="outline" className="gap-1 text-[10px] bg-warning/15 text-warning border-warning/30">
                      <ShieldAlert className="w-3 h-3" /> Unverified outlet
                    </Badge>}
              </div>
              <p className="text-sm text-muted-foreground mt-0.5 truncate">
                {profile?.headline || 'Add a headline to describe your work'}
                {profile?.outlet_name ? ` · ${profile.outlet_name}` : ''}
              </p>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${TIER_STYLES[tier] ?? TIER_STYLES.none}`}>
                  <Award className="w-3 h-3" />
                  {total} pts · {tier === 'none' ? 'Unranked' : tier.charAt(0).toUpperCase() + tier.slice(1)}
                </span>
                {overallRanking && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground">
                    #{overallRanking.overall_rank} of {overallRanking.total_journalists} overall
                  </span>
                )}
                {profile?.nationality && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                    <MapPin className="w-3 h-3" /> {profile.nationality}
                  </span>
                )}
                {(profile?.beats ?? []).slice(0, 4).map(b => (
                  <Badge key={b} variant="secondary" className="text-[10px]">{b}</Badge>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0 sm:pb-1">
              {editing ? (
                <Button variant="outline" size="sm" onClick={state.cancelEdit} className="gap-1.5">
                  <X className="w-3.5 h-3.5" /> Cancel
                </Button>
              ) : (
                <Button size="sm" onClick={() => setEditing(true)} className="gap-1.5">
                  <Pencil className="w-3.5 h-3.5" /> Edit profile
                </Button>
              )}
              {publicUrl && profile?.profile_public && (
                <>
                  <Button
                    variant="outline" size="icon" className="w-9 h-9"
                    aria-label="Copy public profile link"
                    onClick={() => {
                      navigator.clipboard.writeText(publicUrl)
                      setCopied(true); setTimeout(() => setCopied(false), 2000)
                    }}
                  >
                    {copied ? <Check className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
                  </Button>
                  <a
                    href={`/press/${profile.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-border hover:bg-muted transition-colors"
                    aria-label="Open public profile"
                  >
                    <Globe className="w-3.5 h-3.5" />
                  </a>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={Award} label="Coverage score" value={total} />
        <Stat icon={FileText} label="Submissions" value={(submissions ?? []).length} />
        <Stat icon={BadgeCheck} label="Active accreditations" value={activeAccreditations} />
        <Stat icon={Building2} label="Documents uploaded" value={`${docsDone}/3`} />
      </div>
    </div>
  )
}
